import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchResources, fetchValidOptions, fetchFilterTerms, fetchResourcesHtml, saveFiltersToCookie, loadFiltersFromCookie, clearFiltersCookie, generateBlockId } from './api'
import { useDebounce } from './hooks/useDebounce'
import { useUrlState } from './hooks/useUrlState'
import ResourcesFilters from './Filters'
import ResourcesPagination from './Pagination'
import DiveDeeper from './DiveDeeper'
import { ResourceCardSkeleton } from './Skeletons'

const ResourcesList = ({ config, containerId }) => {
  const blockId = useMemo(() => generateBlockId(), [])

  const hasAuthorProfileTerms = useMemo(() => {
    return config.terms && config.terms.some(term =>
      term.term_name === 'author_profile' || term.name === 'author'
    )
  }, [config.terms])

  const hasTerms = useMemo(() => {
    return config.terms && config.terms.length > 0
  }, [config.terms])

  const { query, page, filters, updateQuery, updateQueryImmediate, updatePage, updateFilters } = useUrlState()

  useEffect(() => {
    const cookieFilters = loadFiltersFromCookie(blockId)
    const urlHasFilters = filters && Object.keys(filters).length > 0

    if (cookieFilters && Object.keys(cookieFilters).length > 0 && !urlHasFilters) {
      updateFilters(cookieFilters)
    } else if (urlHasFilters && cookieFilters) {
      saveFiltersToCookie(filters, blockId)
    }
  }, [blockId])

  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      saveFiltersToCookie(filters, blockId)
    }
  }, [filters, blockId])

    // Local query state that doesn't immediately trigger URL updates
  const [localQuery, setLocalQuery] = useState(query)

  // Use a separate state for the actual query term that triggers the search
  const [activeQuery, setActiveQuery] = useState(query)

  // Debounce the local query for automatic triggering (2-3 seconds)
  const debouncedLocalQuery = useDebounce(localQuery, 2500)

  // Track if query is pending (different from active query)
  const isQueryPending = localQuery !== activeQuery && localQuery.length > 0

  const { data: resourcesData, isLoading: resourcesLoading, error: resourcesError, refetch: resourcesRefetch } = useQuery({
    queryKey: ['resources', config.post_type, config.posts_per_page, page, activeQuery, filters, config.exclude_featured, config.terms, config.widget, config.widget_size],
    queryFn: () => fetchResourcesHtml({
      post_type: config.post_type,
      posts_per_page: config.posts_per_page,
      page,
      search: activeQuery,
      filters,
      exclude_featured: config.exclude_featured,
      terms: config.terms,
      card_path: config.card_path,
      widget: config.widget,
      widget_size: config.widget_size
    }),
    staleTime: 2 * 60 * 1000,
  })

  const { data: filterTermsData } = useQuery({
    queryKey: ['filterTerms', config.post_type, config.terms],
    queryFn: () => fetchFilterTerms({
      post_type: config.post_type,
      terms: config.terms
    }),
    staleTime: 10 * 60 * 1000,
    enabled: !!config.post_type && hasTerms,
  })

  const { data: validOptions, isLoading: validOptionsLoading } = useQuery({
    queryKey: ['validOptions', config.post_type, filters, config.exclude_featured, config.terms],
    queryFn: () => fetchValidOptions({
      post_type: config.post_type,
      filters,
      exclude_featured: config.exclude_featured,
      terms: config.terms
    }),
    staleTime: 5 * 60 * 1000,
    enabled: !!config.post_type && hasAuthorProfileTerms,
  })

  const queryClient = useQueryClient()

  // Sync localQuery with URL query when URL changes (e.g., back button, page load)
  // Use a ref to track if we initiated the URL change to avoid sync loops
  const isManualUpdateRef = useRef(false)

  useEffect(() => {
    if (!isManualUpdateRef.current) {
      setLocalQuery(query)
      setActiveQuery(query)
    }
    isManualUpdateRef.current = false
  }, [query])

  // Sync activeQuery with debounced local query (for automatic search after 2.5s)
  useEffect(() => {
    if (debouncedLocalQuery !== activeQuery) {
      isManualUpdateRef.current = true
      setActiveQuery(debouncedLocalQuery)
      // Update URL and page when query actually triggers
      updateQuery(debouncedLocalQuery)
      updatePage(1)
    }
  }, [debouncedLocalQuery, activeQuery, updateQuery, updatePage])

  const handleQueryChange = useCallback((value) => {
    // Only update local query state, don't trigger URL updates immediately
    setLocalQuery(value)
  }, [])

  const handleManualQuery = useCallback(() => {
    // Trigger immediate search with current local query value
    isManualUpdateRef.current = true
    setActiveQuery(localQuery)
    updateQueryImmediate(localQuery)
    updatePage(1)
  }, [localQuery, updateQueryImmediate, updatePage])

  const handleFilterChange = useCallback((termName, values) => {
    const newFilters = { ...filters }

    if (values && Array.isArray(values) && values.length > 0) {
      // Remove duplicates and empty values
      const cleanValues = [...new Set(values.filter(Boolean))]
      newFilters[termName] = cleanValues
    } else {
      // Remove the filter if it has no values
      delete newFilters[termName]
    }

    // Clean up any other empty filters
    Object.keys(newFilters).forEach(key => {
      if (!newFilters[key] || (Array.isArray(newFilters[key]) && newFilters[key].length === 0)) {
        delete newFilters[key]
      }
    })

    updateFilters(newFilters)
    updatePage(1)
    saveFiltersToCookie(newFilters, blockId)
  }, [filters, updateFilters, updatePage, blockId])

  const handlePageChange = useCallback((newPage) => {
    updatePage(newPage);
    queryClient.invalidateQueries(['resources', config.post_type, activeQuery, newPage, filters]);
    document.getElementById('ajax-resources')?.scrollIntoView({ behavior: 'smooth' });
  }, [updatePage, queryClient, config.post_type, activeQuery, filters, blockId]);

  const handleClearAllFilters = useCallback(() => {
    setLocalQuery('')
    setActiveQuery('')
    updateQuery('')
    updateFilters({});
    clearFiltersCookie(blockId);
  }, [updateFilters, blockId]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const hasQuery = activeQuery && activeQuery.length > 0;
    const hasFilters = filters && Object.keys(filters).length > 0 &&
      Object.values(filters).some(values => Array.isArray(values) && values.length > 0);
    return hasQuery || hasFilters;
  }, [activeQuery, filters]);

  if (resourcesError) {
    return (
      <div className="resources-error">
        <p>Error loading resources: {resourcesError.message}</p>
        <button onClick={() => queryClient.invalidateQueries(['resources'])}>
          Try Again
        </button>
      </div>
    )
  }

  return (
    <>
      {config.heading?.title && (
        <div className="resources__header">
          {config.heading.intro && <p className="resources__intro">{config.heading.intro}</p>}
          {config.heading.title && <h2 className="resources__title">{config.heading.title}</h2>}
          {config.heading.description && <p className="resources__description">{config.heading.description}</p>}
        </div>
      )}

      {/* Only show filters if we have terms or search is enabled */}
      {(hasTerms || config.search_placeholder) && (
        <div className="resources__controls grid">
          <ResourcesFilters
            terms={filterTermsData?.terms ? Object.values(filterTermsData.terms) : []}
            selectedFilters={{
              ...filters,
              query: localQuery && localQuery.length > 0 ? localQuery : undefined
            }}
            validOptions={hasAuthorProfileTerms ? (validOptions?.valid_options || {}) : {}}
            onFilterChange={handleFilterChange}
            onClearFilter={(termName) => {
              if (termName === 'query') {
                setLocalQuery('')
                setActiveQuery('')
                updateQuery('')
                return
              }

              const newFilters = { ...filters }
              delete newFilters[termName]

              // Clean up any other empty filters
              Object.keys(newFilters).forEach(key => {
                if (!newFilters[key] || (Array.isArray(newFilters[key]) && newFilters[key].length === 0)) {
                  delete newFilters[key]
                }
              })

              updateFilters(newFilters)
              saveFiltersToCookie(newFilters, blockId)
            }}
            onClearAll={handleClearAllFilters}
            hasActiveFilters={hasActiveFilters}
            filtersLabel={config.label}
            searchPlaceholder={config.search_placeholder}
            isLoading={resourcesLoading}
            searchValue={localQuery}
            onSearchChange={handleQueryChange}
            onManualSearch={handleManualQuery}
            isSearchPending={isQueryPending}
            hasTerms={hasTerms}
          />
        </div>
      )}

      {/* Widget Display - Only for Big size and when no filters are applied */}
      {resourcesData?.widget && resourcesData.widget.size === 'big' && !hasActiveFilters && (
        <div className="resources__widget resources__widget--big">
          <div
            className="resources__widget__content"
            dangerouslySetInnerHTML={{ __html: resourcesData.widget.html }}
          />
        </div>
      )}

      <div className="resources__grid grid">
        <div className="row">

        {resourcesLoading ? (
          <>
            {Array.from({ length: 8 }).map((_, index) => (
              <ResourceCardSkeleton key={`skeleton-${index}`} />
            ))}
          </>
        ) : resourcesData?.cards && resourcesData.cards.length > 0 ? (
          <>
            {resourcesData.cards.map((card) => (
              <div
                key={card.id}
                className="resources__card"
                dangerouslySetInnerHTML={{ __html: card.html }}
              />
            ))}
          </>
        ) : (
          <div className="resources__no--results">
            <p>No resources found matching your criteria.</p>
          </div>
        )}

        </div>
      </div>

      {resourcesData?.pagination && resourcesData.pagination.total > 1 && (
        <ResourcesPagination
          currentPage={page}
          totalPages={resourcesData.pagination.total}
          onPageChange={handlePageChange}
          isLoading={resourcesLoading}
        />
      )}

      {/* Only show DiveDeeper if we have terms */}
      {hasTerms && (
        <DiveDeeper
          config={config}
          filters={filters}
          exclude_featured={config.exclude_featured}
        />
      )}

    </>
  )
}

export default ResourcesList
